import { useState } from "react";
import { CopiedIcon, CopyFailedIcon, CopyIcon } from "./icons.tsx";

/**
 * Копирование адреса: поле со ссылкой и кнопка-значок.
 *
 * ⚠️ **Живёт в `shell`, а не в фиче.** До 03.09.2026 файл лежал в
 * `features/lessons`, и за ним уже ходили комната и ученики; с приходом
 * расписания зовущих мест стало шесть в четырёх фичах, и «общее» перестало
 * опознаваться по адресу. Правки в трёх старых зовущих файлах это ровно и
 * только новый путь импорта.
 *
 * ⚠️ **Сама ссылка показана текстом, а не спрятана за кнопкой, и это не
 * избыточность:** `navigator.clipboard` есть только в защищённом контексте и
 * может быть запрещён политикой браузера. Если копирование не сработало,
 * человек обязан иметь возможность выделить ссылку руками, иначе он потеряет
 * её насовсем: показать код второй раз сервер не может. По той же причине
 * `CopyButton`, у которого поля рядом нет, при отказе ПОКАЗЫВАЕТ адрес:
 * молчащий значок остался бы единственным путём к адресу и вёл бы в тупик.
 *
 * ⚠️ **Выделение из поля это такое же копирование, как нажатие кнопки, и
 * считается наравне с ним** (`onCopy` на самом поле). Поле `readOnly` с
 * выделением по фокусу само приглашает нажать Ctrl+C, и так забирает ссылку
 * тот, кто не заметил кнопку или кому привычнее клавиатура. Без этой строки
 * продукт считал бы ссылку незабранной у человека, который её уже забрал, и
 * дальше приставал бы к нему с предупреждениями.
 *
 * ⚠️ **Видов два, а фигура и поведение одни.** Слово с кнопки убирается там,
 * где рядом уже стоит слово (раздел 8б `docs/cabinet-tables-design.md`): в
 * карточке занятия это «Созвон» и домен, в редакторе серии подпись поля. Там,
 * где ссылку выдают один раз и потерять её нельзя, слово остаётся: этот блок
 * человек читает один раз на ученика, и ребус в нём стоит дороже, чем
 * сэкономленное место.
 */

/** Состояние копирования. Общее у обоих видов, чтобы отказ вёл себя одинаково. */
function useCopy(url: string, onCopied?: () => void) {
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");

  /** Ссылка забрана. Зовётся и кнопкой, и копированием прямо из поля. */
  const succeed = () => {
    setState("done");
    onCopied?.();
    setTimeout(() => setState("idle"), 2000);
  };

  async function copy() {
    try {
      /*
        Отсутствие `navigator.clipboard` целиком (незащищённый контекст) сюда
        же и приезжает: синхронный отказ внутри `async` это отклонённое
        обещание, и ловится он тем же `catch`.
      */
      await navigator.clipboard.writeText(url);
      succeed();
    } catch {
      /*
        Из отказа состояние само не выходит, в отличие от успеха: адрес,
        показанный вместо буфера обмена, это единственный оставшийся путь к
        нему, и погасить его по таймеру значило бы отнять и его тоже.
      */
      setState("failed");
    }
  }

  return { state, succeed, copy: () => void copy() };
}

export function CopyLink({ url, onCopied }: { url: string; onCopied?: () => void }) {
  const { state, succeed, copy } = useCopy(url, onCopied);

  return (
    <div className="copylink">
      <input
        className="fld"
        value={url}
        readOnly
        onFocus={(e) => e.currentTarget.select()}
        onCopy={() => succeed()}
        aria-label="Ссылка"
      />
      <button type="button" className="btn" onClick={copy}>
        {state === "done" ? "Скопировано" : "Копировать"}
      </button>
      {state === "failed" && <p className="note">Не удалось скопировать: выделите ссылку вручную</p>}
    </div>
  );
}

/**
 * То же действие значком: адрес уже назван соседями, слово было бы третьим.
 *
 * ⚠️ Подпись живёт на кнопке (`aria-label` плюс `title`), а не внутри фигуры:
 * это обычная кнопка-значок, а не значок вместо слова, и правило для неё
 * записано в `features/shell/icons.tsx` и `features/schedule/icons.tsx`.
 * Подпись меняется вместе с состоянием, потому что после нажатия человек ждёт
 * ответа именно от той кнопки, на которой стоит его фокус.
 *
 * ⚠️ Нечего копировать это `disabled`, а не отсутствие кнопки. Поле, у которого
 * значок появляется и пропадает по мере набора адреса, дёргается ровно в тот
 * миг, когда адрес стал разбираться; гашение для того в словаре кнопок и
 * заведено.
 */
export function CopyButton({
  url,
  label,
  disabled,
}: {
  url: string;
  label: string;
  disabled?: boolean;
}) {
  const { state, copy } = useCopy(url);
  const name =
    state === "done" ? "Скопировано" : state === "failed" ? "Не удалось скопировать" : label;

  return (
    <>
      <button
        type="button"
        className="btn btn--ico btn--quiet"
        onClick={copy}
        disabled={disabled}
        aria-label={name}
        title={name}
      >
        {state === "done" ? <CopiedIcon /> : state === "failed" ? <CopyFailedIcon /> : <CopyIcon />}
      </button>
      {/*
        ⚠️ Отказ говорит СЛОВАМИ, а не одной фигурой: человеку нужна причина, а
        не категория (раздел 8б `docs/cabinet-tables-design.md`, абзац «Чего
        правило не разрешает»). Адрес рядом с ними обязателен: поля здесь нет, и
        без него забрать его было бы нечем.
      */}
      {state === "failed" && (
        <span className="note">
          Не удалось скопировать. Выделите адрес: <span className="mono-break">{url}</span>
        </span>
      )}
    </>
  );
}
